import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Container,
  Box,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Header } from '../../components/Header/Header';
import { newServer } from 'constants/constants';
import { useNavigate, useParams } from 'react-router-dom';
import { useLogin } from 'Contexts/AuthProvider/AuthProvider';
import Footer from 'components/Footer/Footer';

interface Plan {
  subscribe_rate_id: string;
  fixed_value: string;
  marketing_title: string;
  marketing_description: string;
  feature: string[];
  days_of_trail_period: number;
  chargeusers: any;
}

const SubscriptionPlans = () => {
  const { user, logout } = useLogin();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const { id, subdomain } = useParams<{ id: string; subdomain: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSubscriptions = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await fetch(`${newServer}subscriptions/all`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const result = await response.json();
        if (result.status === 'JWT_EXPIRED') {
          logout();
          navigate('/enroll');
        }
        setPlans(result.data);
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
      }
    };

    fetchSubscriptions();
  }, []);

  /**
   * Propósito: Muestra cupos de usuarios web y staff del plan, si existen.
   */
  const UserTypesDisplay = ({
    chargeusers,
  }: {
    chargeusers: { web?: number; employee?: number };
  }) => {
    if (!chargeusers) return null;
    const hasWeb = chargeusers.web && chargeusers.web > 0;
    const hasStaff = chargeusers.employee && chargeusers.employee > 0;

    if (!hasWeb && !hasStaff) {
      /**
       * Output:
       * @returns null — El plan no cobra usuarios extra
       */
      return null;
    }

    /**
     * Output:
     * @returns JSX — Cupos web/staff con divisor del tema
     */
    return (
      <Box
        sx={{
          my: 3,
          py: 2,
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {hasWeb && (
          <Typography
            color='text.primary'
            sx={{
              textAlign: 'center',
              fontSize: '0.875rem',
              mb: hasStaff ? 1 : 0,
            }}
          >
            <Box component='span' sx={{ fontWeight: 700 }}>{chargeusers.web}</Box> Web Users
          </Typography>
        )}
        {hasStaff && (
          <Typography
            color='text.primary'
            sx={{
              textAlign: 'center',
              fontSize: '0.875rem',
            }}
          >
            <Box component='span' sx={{ fontWeight: 700 }}>{chargeusers.employee}</Box> Staff
          </Typography>
        )}
      </Box>
    );
  };

  /**
   * Propósito: Lista las features del plan con el check de marca (no un azul fijo).
   */
  const FeatureList = ({ features }: { features: string[] }) => {
    if (!features?.length) {
      /**
       * Output:
       * @returns null — El plan no publica features
       */
      return null;
    }

    /**
     * Output:
     * @returns JSX — Lista de features con icono `primary.light` (legible en ambos temas)
     */
    return (
      <List sx={{ mt: 3 }}>
        {features.map((feature, index) => (
          <ListItem
            key={index}
            sx={{
              py: 1,
              px: 0,
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckCircleOutlineIcon sx={{ color: 'primary.light', fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary={feature}
              primaryTypographyProps={{
                sx: {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'text.primary',
                },
              }}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  /**
   * Propósito: Asocia el plan al submerchant y abre el login del portal.
   */
  const handleSelectSubscription = async (subscribe_rate_id: string) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${newServer}subscriptions/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          subscribe_rate_id,
          submerchant_id: id,
          subdomain,
        }),
      });
      const result = await response.json();
      if (result) {
        const baseUrl = process.env.REACT_APP_PLATFORM_BASE_URL || 'paymylawyer.cc';
        /**
         * Output:
         * Side effects — Marca el plan, cierra sesión y redirige al login del subdominio
         */
        window.location.href = `https://${subdomain}.${baseUrl}/login`;
        logout();
        setSelectedPlan(subscribe_rate_id);
      }
    } catch (error) {
      console.error('Error selecting subscription:', error);
    }
  };

  return (
    <div className='bg-background min-h-screen'>
      <Header />
      <Container maxWidth='xl' sx={{ py: 6, bgcolor: 'background.default' }}>
        <Typography
          variant='h3'
          component='h2'
          align='center'
          color='text.primary'
          sx={{ mb: 5, fontWeight: 700 }}
        >
          Choose Your Plan
        </Typography>

        <Grid container spacing={3} justifyContent='center'>
          {plans.map((plan) => {
            const isSelected = selectedPlan === plan.subscribe_rate_id;
            return (
              <Grid
                item
                key={plan.subscribe_rate_id}
                sx={{
                  width: '25%',
                  minWidth: 280,
                  '@media (max-width: 1200px)': {
                    width: '33.33%',
                  },
                  '@media (max-width: 900px)': {
                    width: '50%',
                  },
                  '@media (max-width: 600px)': {
                    width: '100%',
                  },
                }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    overflow: 'visible',
                    cursor: 'pointer',
                    borderWidth: 2,
                    borderStyle: 'solid',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    boxShadow: isSelected ? 6 : 1,
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 8,
                    },
                  }}
                  onClick={() => setSelectedPlan(plan.subscribe_rate_id)}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography
                      variant='h5'
                      align='center'
                      color='text.primary'
                      sx={{ fontWeight: 700, mb: 2 }}
                    >
                      {plan.marketing_title || 'Subscription Plan'}
                    </Typography>

                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                      <Typography
                        component='div'
                        color='text.primary'
                        sx={{
                          fontWeight: 800,
                          fontSize: '2.5rem',
                          display: 'inline-flex',
                          alignItems: 'baseline',
                          gap: 1,
                        }}
                      >
                        ${parseFloat(plan.fixed_value).toFixed(2)}
                        <Typography
                          component='span'
                          color='text.secondary'
                          sx={{ fontSize: '0.875rem' }}
                        >
                          /month
                        </Typography>
                      </Typography>
                    </Box>

                    <UserTypesDisplay chargeusers={plan.chargeusers} />

                    <Button
                      fullWidth
                      size='large'
                      color='primary'
                      variant={isSelected ? 'contained' : 'outlined'}
                      sx={{
                        py: 1.5,
                        fontWeight: 600,
                        mb: 2,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectSubscription(plan.subscribe_rate_id);
                      }}
                    >
                      {isSelected ? 'Current Plan' : 'Select Plan'}
                    </Button>

                    {plan.marketing_description && (
                      <Typography
                        color='text.secondary'
                        sx={{
                          textAlign: 'center',
                          fontSize: '0.875rem',
                          lineHeight: 1.5,
                          fontWeight: 700,
                        }}
                      >
                        {plan.marketing_description}
                      </Typography>
                    )}

                    <FeatureList features={plan.feature} />
                  </CardContent>

                  <CardActions sx={{ p: 0.5, pt: 0.5, mt: 'auto' }}></CardActions>

                  {plan.days_of_trail_period > 0 && (
                    <Typography
                      align='center'
                      color='text.secondary'
                      sx={{
                        pb: 2,
                        fontSize: '0.875rem',
                      }}
                    >
                      {plan.days_of_trail_period} Day free trial to start
                    </Typography>
                  )}
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
      <Footer />
    </div>
  );
};

export default SubscriptionPlans;
